import React from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeedbackCollector, { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SentryErrorBoundary } from '../../../common/sentry';

const styles = cssMap({
	floatingButton: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.200'),
		zIndex: 100,
	},
});

type CollectFeedbackProps = {
	atlassianAccountId: string;
	entrypointId: string;
	name: string | undefined;
	locale: string;
};

export const CollectFeedback = ({
	atlassianAccountId,
	entrypointId,
	name,
	locale = 'en',
}: CollectFeedbackProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [displayFlag, setDisplayFlag] = React.useState(false);

	const handleOpen = React.useCallback(() => setIsOpen(true), []);
	const handleClose = React.useCallback(() => setIsOpen(false), []);
	const handleSubmit = React.useCallback(() => setDisplayFlag(true), []);
	const hideFlag = React.useCallback(() => setDisplayFlag(false), []);

	return (
		<SentryErrorBoundary fallback={null} touchpointId="collect-feedback">
			<Box xcss={styles.floatingButton}>
				<IconButton
					icon={MegaphoneIcon}
					label="Give feedback"
					shape="circle"
					onClick={handleOpen}
					appearance="discovery"
				/>
			</Box>

			{isOpen && (
				<FeedbackCollector
					locale={locale}
					onClose={handleClose}
					onSubmit={handleSubmit}
					atlassianAccountId={atlassianAccountId}
					name={name}
					entrypointId={entrypointId}
				/>
			)}

			<FlagGroup onDismissed={hideFlag}>{displayFlag && <FeedbackFlag />}</FlagGroup>
		</SentryErrorBoundary>
	);
};
